import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AssessmentStatusPill } from "@/components/AssessmentStatusPill";
import SearchOff from "@/assets/search-off.svg?react";
import type { NeedsActionPatient } from "../../domain/entities/NeedsAction";
import {
  desktopPaginatedTableShellClass,
  desktopScrollTableClass,
} from "@/components/layout/tableLayout";
import { textStyles } from "@/lib/textStyles";

interface NeedsActionTableProps {
  items: NeedsActionPatient[];
  isLoading?: boolean;
}

export const NeedsActionTable: React.FC<NeedsActionTableProps> = ({
  items,
  isLoading,
}) => {
  const { t } = useTranslation("needsAction");
  const navigate = useNavigate();

  const handleRowClick = (item: NeedsActionPatient) => {
    navigate(`/patients/${item.id}`);
  };

  const stopRowClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <div className="mt-4">
      <div className="space-y-3 md:hidden">
        {isLoading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="rounded-xl border bg-white p-4">
              <div className="h-4 w-2/3 animate-pulse rounded bg-gray-200" />
              <div className="mt-3 h-3 w-full animate-pulse rounded bg-gray-100" />
              <div className="mt-2 h-3 w-1/2 animate-pulse rounded bg-gray-100" />
            </div>
          ))
        ) : items.length === 0 ? (
          <div className="rounded-xl border bg-white px-4 py-10 text-center">
            <SearchOff className="mx-auto mb-2 text-gray-400" />
            <p className="font-medium text-gray-700">{t("table.noResultsTitle")}</p>
            <p className="mt-1 text-sm text-gray-500">
              {t("table.noResultsBodyMobile")}
            </p>
          </div>
        ) : (
          items.map((item) => (
            <div
              key={item.id}
              className="cursor-pointer rounded-xl border bg-white p-4 transition-colors hover:bg-gray-50"
              onClick={() => handleRowClick(item)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleRowClick(item);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <div className="flex min-w-0 items-center gap-3">
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F2F5FF] text-black"
                  style={{
                    ...textStyles.body,
                    color: "#1B1D21",
                    fontWeight: 700,
                  }}
                >
                  {item.initials}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-gray-900">
                    {item.name}
                  </p>
                  <p className="text-sm text-gray-500">{item.phone}</p>
                </div>
              </div>
              <AssessmentStatusPill
                status={item.currentAssessment.status}
                label={item.statusLabel}
                tone={item.statusType}
                className="mt-3 max-w-full overflow-hidden whitespace-nowrap [&>span]:truncate"
              />
              <div className="mt-4 flex items-end justify-between gap-3">
                <div className="text-sm text-gray-600">
                  <div>{item.formattedStatusAt.absolute}</div>
                  <div className="text-xs text-[#C62828]">
                    ({item.formattedStatusAt.relative})
                  </div>
                </div>
                <button
                  type="button"
                  className="app-text-button"
                  onClick={(event) => {
                    stopRowClick(event);
                    handleRowClick(item);
                  }}
                >
                  {item.actionLabel}
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <div className={desktopPaginatedTableShellClass}>
        <Table className={`${desktopScrollTableClass} min-w-[920px] table-fixed`}>
        <TableHeader>
          <TableRow className="bg-[#D8DDE6] hover:bg-[#D8DDE6]">
            <TableHead className="w-[20%] py-3 px-4 font-bold text-gray-700">
              {t("table.name")}
            </TableHead>
            <TableHead className="w-[17%] py-3 px-4 font-bold text-gray-700">
              {t("table.phone")}
            </TableHead>
            <TableHead className="w-[24%] py-3 px-4 font-bold text-gray-700">
              {t("table.status")}
            </TableHead>
            <TableHead className="w-[18%] py-3 px-4 font-bold text-gray-700">
              {t("table.dateTime")}
            </TableHead>
            <TableHead className="w-[21%] py-3 px-4 text-right font-bold text-gray-700">
              {t("table.nextAction")}
            </TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {isLoading ? (
            Array.from({ length: 8 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell className="py-4 px-4" colSpan={5}>
                  <div className="h-4 w-full rounded bg-gray-200 animate-pulse" />
                </TableCell>
              </TableRow>
            ))
          ) : items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="py-12">
                <div className="flex flex-col items-center justify-center text-center w-full">
                  <SearchOff className="mb-2 text-gray-400" />
                  <p className="font-medium text-gray-700">{t("table.noResultsTitle")}</p>
                  <p className="text-sm text-gray-500 w-72 text-center">
                    {t("table.noResultsBodyDesktopLine1")} <br />
                    {t("table.noResultsBodyDesktopLine2")}
                  </p>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            items.map((item) => (
              <TableRow
                key={item.id}
                className="cursor-pointer hover:bg-gray-50"
                onClick={() => handleRowClick(item)}
              >
                <TableCell className="w-[20%] py-3 px-4 font-medium">
                  <div className="flex min-w-0 items-center gap-2">
                    <span
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#F2F5FF] text-black"
                      style={{
                        ...textStyles.body,
                        color: "#1B1D21",
                        fontWeight: 700,
                      }}
                    >
                      {item.initials}
                    </span>
                    <span className="block min-w-0 truncate" title={item.name}>
                      {item.name}
                    </span>
                  </div>
                </TableCell>

                <TableCell className="w-[17%] py-3 px-4">
                  <span className="block truncate" title={item.phone}>
                    {item.phone}
                  </span>
                </TableCell>

                <TableCell className="w-[24%] py-3 px-4">
                  <AssessmentStatusPill
                    status={item.currentAssessment.status}
                    label={item.statusLabel}
                    tone={item.statusType}
                    className="max-w-full overflow-hidden whitespace-nowrap [&>span]:truncate"
                  />
                </TableCell>

                <TableCell className="w-[18%] py-3 px-4 text-sm text-gray-600">
                  <div
                    className="truncate"
                    title={item.formattedStatusAt.absolute}
                  >
                    {item.formattedStatusAt.absolute}
                  </div>
                  <div
                    style={{ color: "#C62828" }}
                    className="truncate text-xs"
                    title={item.formattedStatusAt.relative}
                  >
                    ({item.formattedStatusAt.relative})
                  </div>
                </TableCell>

                <TableCell className="w-[21%] py-3 px-4 text-right">
                  <span
                    className="app-text-button inline-block max-w-full truncate align-bottom"
                    title={item.actionLabel}
                  >
                    {item.actionLabel}
                  </span>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
        </Table>
      </div>
    </div>
  );
};
